import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  ArrowRight, Check, ChevronDown, Menu, X, Zap,
  DollarSign, Users, Globe, Shield, Cpu, BarChart2, Award, Crown, Key, TrendingUp, Package, Infinity,
} from "lucide-react";

/* ── useReveal hook ────────────────────────────────────────────────────────── */
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ── Plan Data ─────────────────────────────────────────────────────────────── */
const PLANS = [
  {
    id: "bronce", name: "BRONCE", price: "$199", billing: "/mes",
    color: "#CD7F32", glow: "rgba(205,127,50,0.08)",
    icon: Package,
    demo: "50 sitios demo", deployed: "10 sitios desplegados",
    features: ["Panel de administración completo", "IA para generación de sitios", "Soporte por email", "Reportes mensuales"],
    span: "col-span-1",
  },
  {
    id: "plata", name: "PLATA", price: "$299", billing: "/mes",
    color: "#C0C0C0", glow: "rgba(192,192,192,0.1)",
    icon: TrendingUp, badge: "Más popular",
    demo: "100 sitios demo", deployed: "20 sitios desplegados",
    features: ["Panel multi-usuario para equipo", "IA avanzada + modelos premium", "Soporte WhatsApp prioritario", "CRM Kanban integrado", "Analytics avanzadas"],
    span: "col-span-1",
  },
  {
    id: "ilimitado", name: "ILIMITADO", price: "$499", billing: "/mes",
    color: "#ffc700", glow: "rgba(255,199,0,0.1)",
    icon: Infinity,
    demo: "Sitios demo ilimitados", deployed: "40 sitios desplegados",
    features: ["Precio mayorista por escala", "White Label / Marca Blanca", "API de integración propia", "Onboarding personalizado", "SLA + Soporte dedicado 24/7"],
    span: "col-span-1",
  },
];

const BENEFITS = [
  { icon: DollarSign, color: "#ffc700", title: "100% de las ganancias", desc: "Vos ponés el precio. Toda la diferencia entre tu precio y el nuestro es tuya." },
  { icon: Cpu, color: "#0074b7", title: "IA genera los sitios en 5min", desc: "La IA crea sitios completos. Vos los vendés sin saber programar." },
  { icon: Globe, color: "#0074b7", title: "Plataforma lista para usar", desc: "Sin desarrollo. Panel completo desde el día 1." },
  { icon: Users, color: "#FF4F00", title: "Clientes centralizados", desc: "Un panel para ver, crear y gestionar todos tus clientes." },
  { icon: BarChart2, color: "#FF4F00", title: "CRM + seguimiento", desc: "Pipeline Kanban, estado de cada lead, seguimientos automáticos." },
  { icon: Shield, color: "#ffc700", title: "Marca blanca", desc: "En el plan Ilimitado usás tu propio logo y dominio." },
];

const FAQ = [
  { q: "¿Necesito saber programar?", a: "Para nada. La IA hace todo. Vos solo ingresás los datos del cliente y el sitio está listo en 5 minutos." },
  { q: "¿Cuánto puedo cobrarle a mis clientes?", a: "Lo que quieras. Muchos revendedores cobran entre $80 y $200 USD/mes. Toda la diferencia es ganancia pura." },
  { q: "¿Qué diferencia hay entre sitio demo y desplegado?", a: "Los demos son previews para mostrarle al prospecto. Los desplegados son sitios en producción activos, ya vendidos y pagando." },
  { q: "¿Puedo cancelar cuando quiera?", a: "Sí. Sin contratos. Cancelás en cualquier momento desde tu panel." },
  { q: "¿Qué incluye la Franquicia?", a: "Todo el know-how del negocio: scripts de ventas, estrategias probadas, soporte 1:1, marca blanca y 50% de comisiones por cada nuevo revendedor que referís." },
];

/* ── Floating price element (parallax) ────────────────────────────────────── */
function FloatingPrice({ px, py }) {
  return (
    <div
      className="hidden lg:block absolute top-1/2 right-20 xl:right-40 select-none pointer-events-none z-20"
      style={{ transform: `translate(${px * 25}px, ${py * 25 - 60}px)`, transition: "transform 0.15s ease-out" }}
    >
      <div className="relative">
        {/* Outer glow */}
        <div className="absolute inset-0 bg-[#ffc700]/20 blur-3xl rounded-full scale-150" />
        <div className="relative text-center">
          <div className="font-black uppercase leading-none" style={{ fontSize: "clamp(5rem, 10vw, 9rem)", color: "transparent", WebkitTextStroke: "2px rgba(255,199,0,0.2)" }}>
            $199
          </div>
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className="w-8 h-px bg-[#ffc700]/30" />
            <span className="text-[10px] font-black text-[#ffc700]/40 uppercase tracking-widest">desde</span>
            <div className="w-8 h-px bg-[#ffc700]/30" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ────────────────────────────────────────────────────────── */
export default function ResellersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  // Reveal hooks
  const [benefitsRef, benefitsVis] = useReveal(0.1);
  const [plansRef, plansVis] = useReveal(0.1);
  const [faqRef, faqVis] = useReveal(0.1);

  useEffect(() => {
    // Scroll al top cuando se navega a esta página
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.key]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    setParallax({ x, y });
  }, []);

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white overflow-x-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* ══ NAV ════════════════════════════════════════════════════════════════ */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-[#0D0D0D]/95 backdrop-blur-2xl border-b border-white/[0.06]" : ""}`}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          {/* Logo solo, sin texto */}
          <Link to="/">
            <img src="/logo.png" alt="SitiosYA" className="h-16 w-16 object-contain" onError={e => { e.target.style.display="none"; }} />
          </Link>
          {/* Links + botones todos a la derecha */}
          <div className="hidden md:flex items-center gap-3 lg:gap-5">
            <div className="flex items-center gap-3 lg:gap-5 text-sm font-semibold text-gray-400 mr-1">
              <a href="#beneficios" className="hover:text-white transition-colors whitespace-nowrap">Beneficios</a>
              <a href="#planes" className="hover:text-white transition-colors whitespace-nowrap">Planes</a>
              <a href="#faq" className="hover:text-white transition-colors whitespace-nowrap hidden lg:block">FAQ</a>
              <Link to="/" className="hover:text-[#FFC000] transition-colors font-bold whitespace-nowrap">Para comercios</Link>
            </div>
            <button onClick={() => navigate("/login")} data-testid="resellers-nav-login"
              className="bg-[#3199BA] hover:bg-[#2888a8] text-white font-black px-4 py-2.5 rounded-xl text-sm transition-all whitespace-nowrap">
              Ingresar
            </button>
            <button onClick={() => document.getElementById("planes")?.scrollIntoView({behavior:"smooth"})}
              data-testid="resellers-nav-cta"
              className="bg-[#FFC000] hover:bg-[#e6b000] active:scale-[0.97] text-black text-sm font-black px-4 py-2.5 rounded-xl flex items-center gap-1.5 whitespace-nowrap">
              Ser revendedor <ArrowRight size={13} />
            </button>
          </div>
          <button className="md:hidden p-2" onClick={() => setMenuOpen(o => !o)}>
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-[#0D0D0D] border-t border-white/[0.05] px-5 py-5 space-y-3">
            <Link to="/" className="block py-2 text-sm text-gray-300 font-semibold border-b border-white/[0.04]" onClick={() => setMenuOpen(false)}>Para comercios</Link>
            <a href="#planes" className="block py-2 text-sm text-gray-300 font-semibold border-b border-white/[0.04]" onClick={() => setMenuOpen(false)}>Planes</a>
            <a href="#beneficios" className="block py-2 text-sm text-gray-300 font-semibold border-b border-white/[0.04]" onClick={() => setMenuOpen(false)}>Beneficios</a>
            <button onClick={() => { navigate("/login"); setMenuOpen(false); }} className="block py-3 text-sm font-semibold text-gray-300 w-full text-left">Ingresar</button>
            <button onClick={() => { document.getElementById("planes")?.scrollIntoView({behavior:"smooth"}); setMenuOpen(false); }}
              className="w-full bg-[#FFC000] text-black font-black py-3.5 rounded-xl text-sm">Ser revendedor →</button>
          </div>
        )}
      </nav>

      {/* ══ HERO ═══════════════════════════════════════════════════════════════ */}
      <section
        ref={heroRef}
        onMouseMove={handleMouseMove}
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16"
      >
        {/* Background layers */}
        <div className="absolute inset-0 grid-pattern pointer-events-none" />
        <div className="absolute inset-0 noise pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#0074b7]/[0.05] rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#ffc700]/[0.03] rounded-full blur-[120px] pointer-events-none" />

        {/* Floating price element (parallax) */}
        <FloatingPrice px={parallax.x} py={parallax.y} />

        {/* Main copy */}
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 py-20">
          <div className="inline-flex items-center gap-2 border border-[#0074b7]/30 text-[#0074b7] text-[10px] font-black uppercase tracking-[0.2em] px-4 py-2 rounded-full mb-8 animate-pulse">
            <Award size={9} /> Programa de Revendedores · SitiosYA
          </div>

          {/* Glitch H1 */}
          <div className="mb-8 overflow-hidden">
            <h1
              className="glitch-wrapper font-black uppercase leading-[0.85] tracking-[-0.04em] block"
              data-text="VENDÉ SITIOS WEB CON IA."
              style={{ fontSize: "clamp(2.8rem, 7.5vw, 8rem)" }}
            >
              <span className="block text-white">VENDÉ SITIOS</span>
              <span className="block text-white">WEB CON IA.</span>
            </h1>
          </div>
          <h2
            className="font-black uppercase leading-[0.85] tracking-[-0.04em] mb-8"
            style={{ fontSize: "clamp(2.5rem, 7vw, 7.5rem)", WebkitTextStroke: "2px #ffc700", color: "transparent" }}
          >
            GANÁS EL 100%.
          </h2>

          <p className="text-base sm:text-lg text-gray-400 mb-10 max-w-xl leading-relaxed">
            Usá nuestra plataforma para generar y vender sitios web con IA a tus clientes.
            Vos ponés el precio. <strong className="text-white">Toda la ganancia es tuya.</strong>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-12">
            <button onClick={() => document.getElementById("planes")?.scrollIntoView({behavior:"smooth"})} data-testid="resellers-hero-cta"
              className="bg-[#FFC000] hover:bg-[#e6b000] active:scale-[0.97] text-black font-black px-8 py-4 rounded-xl text-base transition-all flex items-center justify-center gap-2">
              Ser revendedor <ArrowRight size={18} />
            </button>
            <a href="#planes" className="flex items-center justify-center gap-2 text-gray-400 hover:text-white font-bold text-sm px-4 py-4 transition-colors border border-white/[0.07] rounded-xl">
              Ver planes <ChevronDown size={16} />
            </a>
          </div>

          {/* Stats row */}
          <div className="flex flex-wrap gap-8 sm:gap-14 pt-8 border-t border-white/[0.06]">
            {[["$199","desde /mes"],["100%","tus ganancias"],["5min","por sitio web"]].map(([v,l]) => (
              <div key={l}>
                <p className="text-3xl sm:text-4xl font-black text-[#ffc700] tracking-tight">{v}</p>
                <p className="text-xs text-gray-500 font-semibold mt-1">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ BENEFICIOS ═════════════════════════════════════════════════════════ */}
      <section id="beneficios" ref={benefitsRef} className="py-24 sm:py-32 section-darker relative overflow-hidden">
        <div className="absolute inset-0 noise pointer-events-none" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className={`reveal-item text-center mb-14 ${benefitsVis ? "visible" : ""}`}>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#0074b7] mb-3">Por qué ser revendedor</p>
            <h2 className="font-black uppercase leading-[0.9]" style={{ fontSize: "clamp(2.2rem, 4.5vw, 4.5rem)" }}>
              Tu negocio de sitios web.<br /><span className="text-gray-600">Listo para arrancar hoy.</span>
            </h2>
          </div>
          {/* 3x2 benefits grid (perfect 6 items) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {BENEFITS.map(({ icon: Icon, color, title, desc }, i) => (
              <div
                key={title}
                className={`reveal-item delay-${(i % 3) + 1} ${benefitsVis ? "visible" : ""} group relative bg-[#111] border border-white/[0.06] rounded-2xl p-6 hover:border-white/[0.14] hover:bg-[#141414] transition-all duration-300 overflow-hidden`}
              >
                <div className="absolute top-0 right-0 w-28 h-28 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: color + "15" }} />
                <div className="relative">
                  <div className="mb-4 inline-flex p-2.5 rounded-xl border" style={{ background: color + "15", borderColor: color + "25" }}>
                    <Icon size={18} style={{ color }} />
                  </div>
                  <h3 className="font-bold text-white text-sm mb-2">{title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PLANES ═════════════════════════════════════════════════════════════ */}
      <section id="planes" ref={plansRef} className="py-24 sm:py-36 section-dark relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className={`reveal-item text-center mb-14 ${plansVis ? "visible" : ""}`}>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#ffc700] mb-3">Planes</p>
            <h2 className="font-black uppercase leading-[0.9]" style={{ fontSize: "clamp(2.5rem, 5vw, 5rem)" }}>Elegí tu plan.</h2>
            <p className="text-sm text-gray-500 mt-3 max-w-md mx-auto">Todos incluyen acceso completo a la plataforma. Escalás cuando querés.</p>
          </div>

          {/* 3 standard plans — left+center normal, right spans 2 cols */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {PLANS.map((plan, i) => {
              const PlanIcon = plan.icon;
              return (
                <div
                  key={plan.id}
                  className={`${plan.span} reveal-item delay-${i + 1} ${plansVis ? "visible" : ""} relative rounded-3xl p-6 sm:p-7 flex flex-col border transition-all duration-300 hover:scale-[1.01] hover:shadow-[0_20px_60px_rgba(0,0,0,0.4)]`}
                  style={{
                    background: `linear-gradient(145deg, ${plan.glow} 0%, #111 60%)`,
                    borderColor: plan.color + "25",
                  }}
                >
                  {plan.badge && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 text-black text-[9px] font-black px-4 py-1 rounded-full uppercase tracking-wider whitespace-nowrap"
                      style={{ backgroundColor: plan.color }}>
                      {plan.badge}
                    </div>
                  )}
                  <div className="mb-4 inline-flex p-2.5 rounded-xl border self-start" style={{ background: plan.color + "15", borderColor: plan.color + "25" }}>
                    <PlanIcon size={18} style={{ color: plan.color }} />
                  </div>
                  <h3 className="font-black text-xl text-white mb-1">{plan.name}</h3>
                  <div className="flex items-end gap-1.5 mb-1">
                    <span className="text-4xl sm:text-5xl font-black text-white tracking-tight">{plan.price}</span>
                    <span className="text-gray-400 font-medium mb-1 text-sm">{plan.billing}</span>
                  </div>
                  <div className="mb-4">
                    <p className="text-sm font-bold" style={{ color: plan.color }}>{plan.demo}</p>
                    <p className="text-xs text-gray-500">{plan.deployed}</p>
                  </div>
                  <div className="space-y-2.5 flex-1 mb-6">
                    {plan.features.map(f => (
                      <div key={f} className="flex items-start gap-2 text-xs text-gray-300">
                        <Check size={12} className="flex-shrink-0 mt-0.5" style={{ color: plan.color }} />
                        {f}
                      </div>
                    ))}
                  </div>
                  <button onClick={() => navigate("/login")} data-testid={`plan-cta-${plan.id}`}
                    className="btn-shimmer w-full py-3 rounded-xl font-black text-sm transition-all text-black"
                    style={{ backgroundColor: plan.color }}>
                    Empezar con {plan.name[0] + plan.name.slice(1).toLowerCase()}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Franchise plan — full width, dramatic */}
          <div
            className={`reveal-item delay-4 ${plansVis ? "visible" : ""} relative rounded-3xl overflow-hidden border border-[#FF4F00]/25 p-8 sm:p-12`}
            style={{ background: "linear-gradient(135deg, rgba(255,79,0,0.07) 0%, #111 40%, rgba(255,199,0,0.04) 100%)" }}
          >
            <div className="absolute inset-0 noise pointer-events-none" />
            <div className="absolute top-0 right-0 w-72 h-72 bg-[#ffc700]/[0.05] rounded-full translate-x-1/3 -translate-y-1/3 blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#FF4F00]/[0.06] rounded-full -translate-x-1/2 translate-y-1/2 blur-3xl pointer-events-none" />

            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#FF4F00]/10 border border-[#FF4F00]/25 text-[#FF4F00] text-[10px] font-black uppercase tracking-[0.2em] px-4 py-2 rounded-full mb-5">
                  <Crown size={9} /> Nivel Superior
                </div>
                <h3 className="font-black uppercase leading-[0.85] text-white mb-3" style={{ fontSize: "clamp(2.5rem, 5vw, 5rem)" }}>
                  ¡Comprá una<br /><span className="text-[#ffc700]">Franquicia!</span>
                </h3>
                <p className="text-sm text-gray-400 mb-6 leading-relaxed max-w-sm">
                  Todo el know-how del negocio de venta de sitios web, marca blanca completa y la posibilidad de crear tu propia red de revendedores.
                </p>
                <div className="flex flex-wrap items-center gap-4 sm:gap-6 mb-6">
                  <div>
                    <p className="text-4xl font-black text-white tracking-tight">$1.500</p>
                    <p className="text-xs text-gray-500">USD · pago único</p>
                  </div>
                  <div className="text-gray-600 font-bold text-xl">+</div>
                  <div>
                    <p className="text-4xl font-black text-[#ffc700] tracking-tight">$499</p>
                    <p className="text-xs text-gray-500">USD/mes</p>
                  </div>
                </div>
                <button onClick={() => navigate("/login")} data-testid="plan-cta-franquicia"
                  className="btn-shimmer bg-[#FF4F00] hover:bg-orange-600 active:scale-[0.97] text-white font-black px-8 py-4 rounded-xl text-sm transition-all flex items-center gap-2 shadow-[0_0_30px_rgba(255,79,0,0.2)]">
                  Quiero la Franquicia <ArrowRight size={16} />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { icon: Key, color: "#ffc700", title: "Know-how completo", desc: "Scripts, estrategias probadas y onboarding 1:1 con el equipo SitiosYA." },
                  { icon: Shield, color: "#0074b7", title: "Marca blanca total", desc: "Vendé bajo tu propio nombre, logo y dominio. 100% tuyo." },
                  { icon: DollarSign, color: "#FF4F00", title: "50% de comisiones", desc: "Por cada revendedor que referís, te llevás el 50% de su suscripción mensual." },
                  { icon: Users, color: "#ffc700", title: "Tu red de revendedores", desc: "Construí un equipo. Vos revendés + tu red revende. Ingreso pasivo." },
                ].map(({ icon: Icon, color, title, desc }) => (
                  <div key={title} className="flex gap-3 bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 hover:bg-white/[0.05] transition-colors">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: color + "15" }}>
                      <Icon size={15} style={{ color }} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white mb-0.5">{title}</p>
                      <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ CÓMO FUNCIONA ══════════════════════════════════════════════════════ */}
      <section className="py-24 section-darker relative overflow-hidden">
        <div className="absolute inset-0 noise pointer-events-none" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <h2 className="font-black uppercase" style={{ fontSize: "clamp(2rem, 4vw, 4rem)" }}>¿Cómo funciona?</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              ["01", "Creás tu cuenta", "Elegís tu plan de revendedor y accedés al panel."],
              ["02", "Cargás un cliente", "Ingresás los datos del negocio en la plataforma."],
              ["03", "IA genera el sitio", "En 5 minutos, el sitio está listo para mostrar."],
              ["04", "Cobrás y ganás", "Cobrale a tu cliente. Toda la diferencia es tuya."],
            ].map(([num, title, desc]) => (
              <div key={num} className="relative bg-[#111] border border-white/[0.06] rounded-2xl p-5 hover:border-[#ffc700]/20 hover:bg-[#141414] transition-all group">
                <div className="absolute top-3 right-4 text-5xl font-black text-white/[0.03] select-none group-hover:text-white/[0.06] transition-colors">{num}</div>
                <div className="text-[#ffc700] text-[10px] font-black uppercase tracking-widest mb-3">{num}</div>
                <h3 className="font-bold text-white text-sm mb-2">{title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FAQ ════════════════════════════════════════════════════════════════ */}
      <section id="faq" ref={faqRef} className="py-24 section-dark relative overflow-hidden">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          <div className={`reveal-item text-center mb-12 ${faqVis ? "visible" : ""}`}>
            <h2 className="font-black uppercase" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>Preguntas frecuentes</h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <div
                key={item.q}
                className={`reveal-item delay-${(i % 3) + 1} ${faqVis ? "visible" : ""} bg-[#111] border border-white/[0.06] rounded-2xl overflow-hidden hover:border-white/[0.1] transition-colors`}
              >
                <button
                  className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-[#151515] transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="text-sm font-bold text-white">{item.q}</span>
                  <ChevronDown size={16} className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 border-t border-white/[0.05]">
                    <p className="text-sm text-gray-400 leading-relaxed pt-4">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FOOTER INMERSIVO ═══════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden py-20 sm:py-28 section-darker noise">
        <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
        {/* Massive background text */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center select-none pointer-events-none"
          style={{
            fontSize: "clamp(4rem, 14vw, 13rem)",
            lineHeight: 0.85,
            fontWeight: 900,
            letterSpacing: "-0.04em",
            color: "rgba(255,199,0,0.04)",
            textAlign: "center",
            textTransform: "uppercase",
          }}
        >
          <span>GANÁS</span>
          <span>EL 100%.</span>
        </div>
        {/* CTA content */}
        <div className="relative z-10 max-w-2xl mx-auto text-center px-5">
          <div className="w-16 h-16 bg-[#ffc700]/10 border border-[#ffc700]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <DollarSign size={28} className="text-[#ffc700]" />
          </div>
          <h2 className="font-black uppercase leading-[0.9] mb-5" style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}>
            Empezá a vender sitios.<br />
            <span className="text-[#ffc700]">Ganancias 100% tuyas.</span>
          </h2>
          <p className="text-gray-400 mb-8 text-sm leading-relaxed">Más de 50 revendedores ya usan la plataforma. Planes desde $199 USD/mes.</p>
          <button onClick={() => navigate("/login")} data-testid="resellers-final-cta"
            className="btn-shimmer bg-[#ffc700] hover:bg-[#e6b400] active:scale-[0.98] text-black font-black px-10 py-4 rounded-2xl text-base transition-all inline-flex items-center gap-2">
            Quiero ser revendedor <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* ══ FOOTER ══════════════════════════════════════════════════════════════ */}
      <footer className="border-t border-white/[0.04] py-8 px-5 bg-[#080808]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="SitiosYA" className="h-7 w-7 object-contain" onError={e => { e.target.style.display="none"; }} />
            <span className="font-black text-sm">Sitios<span className="text-[#ffc700]">YA</span></span>
          </Link>
          <div className="flex items-center gap-6 text-xs text-gray-500">
            <Link to="/" className="hover:text-white transition-colors">Para comercios</Link>
            <button onClick={() => navigate("/login")} className="hover:text-white transition-colors">Ingresar</button>
          </div>
          <p className="text-xs text-gray-600">© 2026 SitiosYA · sitiosya.com.uy</p>
        </div>
      </footer>
    </div>
  );
}
